import * as React from 'react';

import { CalendarDate, type Time } from '@internationalized/date';
import { Button, cn, Col, Row, SmartDatePicker } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import startCase from 'lodash/startCase';
import { LucideChevronDown, LucideUndo2 } from 'lucide-react';

import { useTranslations } from '../../i18n/useTranslations';

import { BufferedTimeField } from './BufferedTimeField';
import type {
  FlatTierRow,
  NewInlineRowData,
} from './hooks/useEditablePricingTable';
import type { TierEditValues } from './hooks/useProductPricingColumns';

import { $sidebarWidth } from '~/app/state/sidebar.state';

type PricingSaveToolbarProps = {
  pendingCount: number;
  pendingChanges: Map<string, TierEditValues>;
  flatTierRows: FlatTierRow[];
  effectiveDate: CalendarDate;
  effectiveTime: Time;
  onDateChange: (date: CalendarDate) => void;
  onTimeChange: (time: Time) => void;
  onSave: () => void;
  onDiscard: () => void;
  onRevertRow: (tierId: string) => void;
  newInlineRows: NewInlineRowData[];
  isCRV: boolean;
};

const priceFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 6,
  useGrouping: false,
});

const formatPrice = (val: string | undefined) => {
  if (!val) return '—';
  const num = Number(val);
  if (isNaN(num)) return val;
  return priceFormatter.format(num);
};

type CollapsedToolbarProps = {
  pendingCount: number;
  isRevertConfirmOpen: boolean;
  setIsRevertConfirmOpen: (open: boolean) => void;
  onDiscard: () => void;
  onExpand: () => void;
};

//
// ⚛️ ⚛️ ⚛️ COLLAPSED TOOLBAR ⚛️ ⚛️ ⚛️
//
const CollapsedToolbar = ({
  pendingCount,
  isRevertConfirmOpen,
  setIsRevertConfirmOpen,
  onDiscard,
  onExpand,
}: CollapsedToolbarProps) => {
  // Translations
  const t = useTranslations();

  if (isRevertConfirmOpen) {
    return (
      <Row
        gap1
        className="@CollapsedToolbar pointer-events-auto rounded bg-neutral px-1 py-1 text-sm text-white shadow-lg"
      >
        <span className="mx-2 rounded bg-warning px-2 text-base text-white">
          {pendingCount}
        </span>
        <span className="flex-1 text-white/80">
          {t.pricing.saveToolbar.confirmRevert.description}
        </span>
        <Row gap1>
          <Button
            variant="outline"
            color="neutral"
            onPress={() => setIsRevertConfirmOpen(false)}
          >
            {t.pricing.saveToolbar.confirmRevert.keep}
          </Button>
          <Button
            variant="solid"
            color="failure"
            onPress={() => {
              setIsRevertConfirmOpen(false);
              onDiscard();
            }}
          >
            {t.pricing.saveToolbar.confirmRevert.confirm}
          </Button>
        </Row>
      </Row>
    );
  }

  return (
    <Row
      gap1
      className="@CollapsedToolbar pointer-events-auto rounded bg-neutral px-1 py-1 text-sm text-white shadow-lg"
    >
      <span className="mx-2 rounded bg-warning px-2 text-base text-white">
        {pendingCount}
      </span>
      <span className="flex-1 text-white/80">
        {t.pricing.saveToolbar.reviewMessage}
      </span>
      <Button variant="solid" color="primary" onPress={onExpand}>
        {t.pricing.saveToolbar.reviewAndSave}
      </Button>
    </Row>
  );
};

type ExpandedToolbarProps = {
  pendingCount: number;
  changedRows: FlatTierRow[];
  pendingChanges: Map<string, TierEditValues>;
  newInlineRows: NewInlineRowData[];
  isCRV: boolean;
  effectiveDate: CalendarDate;
  effectiveTime: Time;
  onDateChange: (date: CalendarDate) => void;
  onTimeChange: (time: Time) => void;
  onSave: () => void;
  onRevertRow: (tierId: string) => void;
  onCollapse: () => void;
  onRevert: () => void;
};

//
// ⚛️ ⚛️ ⚛️ EXPANDED TOOLBAR ⚛️ ⚛️ ⚛️
//
const ExpandedToolbar = ({
  pendingCount,
  changedRows,
  pendingChanges,
  newInlineRows,
  isCRV,
  effectiveDate,
  effectiveTime,
  onDateChange,
  onTimeChange,
  onSave,
  onRevertRow,
  onCollapse,
  onRevert,
}: ExpandedToolbarProps) => {
  // Translations
  const t = useTranslations();

  return (
    <div className="@ExpandedToolbar pointer-events-auto w-full max-w-4xl rounded-lg bg-white shadow-lg">
      <Row
        gap1
        className="rounded-t-lg bg-neutral px-3 py-2.5 text-sm text-white"
      >
        <span className="mx-1 rounded bg-warning px-2 text-base text-white">
          {pendingCount}
        </span>
        <span className="flex-1 text-white/80">
          {t.pricing.saveToolbar.reviewMessage}
        </span>
        <button
          className="flex size-6 items-center justify-center rounded text-white/70 hover:text-white"
          onClick={onCollapse}
        >
          <LucideChevronDown className="size-4" />
        </button>
      </Row>

      {changedRows.length > 0 && (
        <div className="max-h-[200px] overflow-auto bg-white">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-neutral/20 bg-neutral/5">
                <th className="p-2 text-left font-semibold">
                  {t.pricing.itemPricings.table.columns.product}
                </th>
                <th className="p-2 text-left font-semibold">
                  {t.pricing.itemPricings.table.columns.pricingUnit}
                </th>
                {!isCRV && (
                  <>
                    <th className="p-2 text-right font-semibold">
                      {t.pricing.itemPricings.table.columns.minWeight}
                    </th>
                    <th className="p-2 text-right font-semibold">
                      {t.pricing.itemPricings.table.columns.maxAmount}
                    </th>
                    <th className="p-2 text-right font-semibold">
                      {t.pricing.itemPricings.table.columns.minPrice}
                    </th>
                    <th className="p-2 text-right font-semibold">
                      {t.pricing.itemPricings.table.columns.targetPrice}
                    </th>
                    <th className="p-2 text-right font-semibold">
                      {t.pricing.itemPricings.table.columns.maxPrice}
                    </th>
                  </>
                )}
                {isCRV && (
                  <th className="p-2 text-right font-semibold">
                    {t.pricing.itemPricings.table.columns.scrapValue}
                  </th>
                )}
                <th className="w-10 p-2" />
              </tr>
            </thead>
            <tbody>
              {changedRows.map((row) => {
                const pending = pendingChanges.get(row.id);
                const inlineRow = newInlineRows.find((nr) => nr.id === row.id);
                const isNew = row.isNewRow && !pending;
                const tierVals = pending ?? inlineRow?.tierEditValues ?? null;
                const orig = row.tier;
                return (
                  <tr
                    key={row.id}
                    className={cn(
                      'border-b border-neutral/10',
                      row.isPendingDelete && 'bg-failure/5',
                    )}
                  >
                    <td className="p-2">
                      <ProductNameCell
                        row={row}
                        isNew={isNew}
                        tierVals={tierVals}
                      />
                    </td>
                    <td className="p-2">
                      {isNew ? (
                        <span>{startCase(row.pricingUnit)}</span>
                      ) : (
                        <ChangeCell
                          oldVal={startCase(row.pricingUnit)}
                          newVal={startCase(
                            tierVals?.pricingUnit ?? row.pricingUnit,
                          )}
                        />
                      )}
                    </td>
                    {!isCRV && (
                      <>
                        <td className="p-2 text-right text-neutral/60">
                          {row.minWeight ?? ''}
                        </td>
                        <td className="p-2 text-right">
                          <ChangeCell
                            oldVal={
                              orig?.maxAmount != null
                                ? String(orig.maxAmount)
                                : ''
                            }
                            newVal={tierVals?.maxAmount ?? ''}
                          />
                        </td>
                        <td className="p-2 text-right">
                          <ChangeCell
                            oldVal={orig?.minPrice.formula ?? ''}
                            newVal={tierVals?.minPrice ?? ''}
                            isPrice
                          />
                        </td>
                        <td className="p-2 text-right">
                          <ChangeCell
                            oldVal={orig?.targetPrice.formula ?? ''}
                            newVal={tierVals?.targetPrice ?? ''}
                            isPrice
                          />
                        </td>
                        <td className="p-2 text-right">
                          <ChangeCell
                            oldVal={orig?.maxPrice.formula ?? ''}
                            newVal={tierVals?.maxPrice ?? ''}
                            isPrice
                          />
                        </td>
                      </>
                    )}
                    {isCRV && (
                      <td className="p-2 text-right">
                        <ChangeCell
                          oldVal={orig ? String(orig.scrapValue) : ''}
                          newVal={tierVals?.scrapValue ?? ''}
                          isPrice
                        />
                      </td>
                    )}
                    <td className="p-2">
                      <button
                        className="flex size-6 items-center justify-center rounded text-neutral/60 hover:bg-neutral/10"
                        onClick={() => onRevertRow(row.id)}
                      >
                        <LucideUndo2 className="size-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Row
        gap3
        className="rounded-b-lg border-t border-neutral/20 bg-white px-4 py-2"
      >
        <SmartDatePicker
          value={effectiveDate}
          onChange={(date) => {
            if (date) onDateChange(date);
          }}
          className="w-40"
        />
        <BufferedTimeField
          aria-label={t.pricing.saveToolbar.effectiveTime}
          value={effectiveTime}
          onChange={onTimeChange}
        />
        <div className="flex-1" />
        <Button variant="solid" color="primary" onPress={onSave}>
          {t.pricing.saveToolbar.activate}
        </Button>
        <Button variant="outline" color="neutral" onPress={onRevert}>
          {t.pricing.saveToolbar.revert}
        </Button>
      </Row>
    </div>
  );
};

//
// ⚛️ ⚛️ ⚛️ PRICING SAVE TOOLBAR ⚛️ ⚛️ ⚛️
//
export const PricingSaveToolbar = ({
  pendingCount,
  pendingChanges,
  flatTierRows,
  effectiveDate,
  effectiveTime,
  onDateChange,
  onTimeChange,
  onSave,
  onDiscard,
  onRevertRow,
  newInlineRows,
  isCRV,
}: PricingSaveToolbarProps) => {
  // States
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isRevertConfirmOpen, setIsRevertConfirmOpen] = React.useState(false);

  // Atoms
  const sidebarWidth = useAtomValue($sidebarWidth) ?? 216;

  // Memoized values
  const changedRows = React.useMemo(
    () =>
      flatTierRows.filter(
        (row) =>
          pendingChanges.has(row.id) ||
          (row.isNewRow && row.id !== 'empty-bottom' && row.itemName) ||
          (row.isPendingDelete && row.isFirstTierOfProduct),
      ),
    [flatTierRows, pendingChanges],
  );

  if (!changedRows.length) {
    return null;
  }

  return (
    <Col
      xCenter
      gap2
      className="@PricingSaveToolbar pointer-events-none fixed right-0 bottom-8 z-50"
      style={{ left: sidebarWidth }}
    >
      {!isExpanded ? (
        <CollapsedToolbar
          pendingCount={pendingCount}
          isRevertConfirmOpen={isRevertConfirmOpen}
          setIsRevertConfirmOpen={setIsRevertConfirmOpen}
          onDiscard={onDiscard}
          onExpand={() => setIsExpanded(true)}
        />
      ) : (
        <ExpandedToolbar
          pendingCount={pendingCount}
          changedRows={changedRows}
          pendingChanges={pendingChanges}
          newInlineRows={newInlineRows}
          isCRV={isCRV}
          effectiveDate={effectiveDate}
          effectiveTime={effectiveTime}
          onDateChange={onDateChange}
          onTimeChange={onTimeChange}
          onSave={onSave}
          onRevertRow={onRevertRow}
          onCollapse={() => setIsExpanded(false)}
          onRevert={() => {
            setIsExpanded(false);
            setIsRevertConfirmOpen(true);
          }}
        />
      )}
    </Col>
  );
};

interface ProductNanoCellProps {
  row: FlatTierRow;
  isNew: boolean;
  tierVals: TierEditValues | null;
}

//
// ⚛️ ⚛️ ⚛️ PRODUCT NAME CELL ⚛️ ⚛️ ⚛️
//
const ProductNameCell = ({ row, isNew, tierVals }: ProductNanoCellProps) => {
  if (row.isPendingDelete) {
    return (
      <span className="font-medium text-failure line-through">
        {row.itemName}
      </span>
    );
  }

  if (isNew) {
    return <span className="font-medium">{row.itemName}</span>;
  }

  return (
    <ChangeCell
      oldVal={row.itemName}
      newVal={tierVals?.itemName ?? row.itemName}
      className="@ProductNameCell"
    />
  );
};

//
// ⚛️ ⚛️ ⚛️ CHANGE CELL ⚛️ ⚛️ ⚛️
//
type ChangeCellProps = {
  oldVal: string;
  newVal: string;
  isPrice?: boolean;
  className?: string;
};

const ChangeCell = ({
  oldVal,
  newVal,
  isPrice,
  className,
}: ChangeCellProps) => {
  const changed = oldVal !== newVal;
  if (!changed) {
    return (
      <span className={className}>
        {isPrice ? formatPrice(newVal) : newVal || '—'}
      </span>
    );
  }
  const oldDisplay = isPrice ? formatPrice(oldVal) : oldVal || '—';
  const newDisplay = isPrice ? formatPrice(newVal) : newVal || '—';
  return (
    <span className={cn('@ChangeCell', className)}>
      <span className={cn('text-neutral/40', oldVal && 'line-through')}>
        {oldDisplay}
      </span>{' '}
      <span className="font-medium">{newDisplay}</span>
    </span>
  );
};
